import types from '../../mutation-types';
import getters, { getSelectedChatConversation } from './getters';
import actions from './actions';
import { findPendingMessageIndex, isStaleMessage } from './helpers';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

// Quantas conversas o WebSocket pode manter EM PÉ na lista. 1.000 = 40 páginas
// do fetch, muito além do que um atendente lê sem rolar.
//
// simplificação: teto fixo com descarte da menos ativa. O que trava a aba não é
// o array existir, é o filter+sort que getFilteredConversations/getMineChats/
// getUnAssignedChats fazem sobre ele INTEIRO a cada evento — e os dois últimos
// devolvem função, então nem o cache de getter do Vuex têm. Com 19.785 conversas
// e um evento por conversa (conta 35, campanha), isso é o produto das duas
// coisas. Caminho de upgrade: tornar esses getters incrementais (índice ordenado
// mantido na mutação); aí o N deixa de importar e este teto sai inteiro.
export const MAX_REALTIME_CONVERSATION_INSERTS = 1000;

const state = {
  allConversations: [],
  // Ledger do teto: ids que entraram na lista por EVENTO (WebSocket) e ainda
  // estão nela, ORDENADOS DA MENOS PARA A MAIS ATIVA — Set em JS preserva ordem
  // de inserção, e quem tem atividade é reinserido no fim (delete+add, O(1)).
  // Assim o descarte pega sempre a ponta fria. O fetch não entra aqui: conversa
  // que o atendente trouxe rolando nunca é descartada.
  realtimeInsertedIds: new Set(),
  // Ids que vieram de uma menção real (fetch com conversation_type=mention ou
  // evento conversation.mentioned) — não confundir com allConversations, que
  // pode ganhar QUALQUER conversa via evento em tempo real (assignee/status/
  // etc). A aba Menções usa este Set pra decidir o que exibir de verdade.
  mentionedConversationIds: new Set(),
  // Mesmo papel do ledger de menções, pra aba Participando (mesmo fail-open:
  // evento em tempo real empurrava conversa alheia pra lista e a aba exibia).
  participatingConversationIds: new Set(),
  attachments: {},
  listLoadingStatus: true,
  chatStatusFilter: wootConstants.STATUS_TYPE.OPEN,
  chatSortFilter: wootConstants.SORT_BY_TYPE.LATEST,
  currentInbox: null,
  selectedChatId: null,
  appliedFilters: [],
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  conversationFilters: {},
  copilotAssistant: {},
  debugSql: '',
};

// A conversa aberta na tela NUNCA é descartada; fora ela, sai a da ponta fria
// do ledger (a que há mais tempo não tem atividade).
const nextEvictableId = _state => {
  const ids = _state.realtimeInsertedIds.values();
  let next = ids.next();
  while (!next.done) {
    if (next.value !== _state.selectedChatId) return next.value;
    next = ids.next();
  }
  return null;
};

// Marca atividade: a conversa volta pro fim da fila e deixa de ser candidata a
// descarte. Sem isto o descarte seria por ordem de CHEGADA e levaria embora
// justamente a conversa de campanha que o cliente acabou de responder.
export const touchRealtimeConversation = (_state, conversationId) => {
  if (!_state.realtimeInsertedIds.delete(conversationId)) return;

  _state.realtimeInsertedIds.add(conversationId);
};

// Único ponto de inserção por EVENTO (WebSocket) — `conversation.created` e
// `conversation.updated` de conversa que ainda não está na lista são o MESMO
// caminho. Quem decide se a conversa entra é a action, com o predicado do
// render (shouldInsertConversationFromEvent); aqui a linha não se duplica na
// reentrega e o teto é mantido por DESCARTE, nunca recusando a conversa nova:
// a lista não pode parar de atualizar sozinha.
const insertConversationFromEvent = (_state, conversation) => {
  if (_state.allConversations.some(c => c.id === conversation.id)) return;

  _state.allConversations.push(conversation);
  _state.realtimeInsertedIds.add(conversation.id);

  if (_state.realtimeInsertedIds.size <= MAX_REALTIME_CONVERSATION_INSERTS) {
    return;
  }

  const evictedId = nextEvictableId(_state);
  if (evictedId === null) return;

  _state.realtimeInsertedIds.delete(evictedId);
  _state.allConversations = _state.allConversations.filter(
    c => c.id !== evictedId
  );
};

// mutations
export const mutations = {
  [types.SET_DEBUG_SQL](_state, sql) {
    _state.debugSql = sql;
  },
  [types.SET_ALL_CONVERSATION](_state, conversationList) {
    const newAllConversations = [..._state.allConversations];
    conversationList.forEach(conversation => {
      const indexInCurrentList = newAllConversations.findIndex(
        c => c.id === conversation.id
      );
      if (indexInCurrentList < 0) {
        newAllConversations.push(conversation);
      } else if (conversation.id !== _state.selectedChatId) {
        // If the conversation is already in the list, replace it
        // Added this to fix the issue of the conversation not being updated
        // When reconnecting to the websocket. If the selectedChatId is not the same as
        // the conversation.id in the store, replace the existing conversation with the new one
        newAllConversations[indexInCurrentList] = conversation;
      } else {
        // If the conversation is already in the list and selectedChatId is the same,
        // replace all data except the messages array, attachments, dataFetched, allMessagesLoaded
        const existingConversation = newAllConversations[indexInCurrentList];
        newAllConversations[indexInCurrentList] = {
          ...conversation,
          allMessagesLoaded: existingConversation.allMessagesLoaded,
          messages: existingConversation.messages,
          dataFetched: existingConversation.dataFetched,
        };
      }
    });
    _state.allConversations = newAllConversations;
  },
  [types.EMPTY_ALL_CONVERSATION](_state) {
    _state.allConversations = [];
    _state.selectedChatId = null;
    // Lista refeita do zero (trocar de aba/filtro/inbox, resetAndFetchData).
    _state.realtimeInsertedIds.clear();
    // Os ledgers (mentioned/participating) NÃO são limpos aqui de propósito:
    // o EMPTY dispara a cada troca de aba com o refetch ainda em voo, e um
    // evento em tempo real nessa janela re-adicionava a conversa em
    // allConversations SEM repor o ledger — menção real ficava invisível o
    // resto da sessão. Os Sets guardam só ids (globais, únicos entre contas)
    // de menção/participação REAL — manter entre resets é o comportamento
    // certo; o preço é ~bytes por conversa mencionada.
  },

  [types.ADD_MENTIONED_CONVERSATION_IDS](_state, ids) {
    ids.forEach(id => _state.mentionedConversationIds.add(id));
  },

  [types.ADD_PARTICIPATING_CONVERSATION_IDS](_state, ids) {
    ids.forEach(id => _state.participatingConversationIds.add(id));
  },
  [types.SET_ALL_MESSAGES_LOADED](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.allMessagesLoaded = true;
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.allMessagesLoaded = false;
  },
  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (data.length) {
      const [chat] = _state.allConversations.filter(c => c.id === id);
      chat.messages.unshift(...data);
    }
  },
  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    _state.attachments[id] = [...data];
  },
  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;
    chat.messages = data;
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
    }
  },

  [types.ASSIGN_AGENT](_state, assignee) {
    const [chat] = getSelectedChatConversation(_state);
    chat.meta.assignee = assignee;
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.meta.team = team;
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.last_activity_at = lastActivityAt;
      // Mensagem nova = conversa viva; sai da mira do descarte.
      touchRealtimeConversation(_state, conversationId);
    }
  },
  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.priority = priority;
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](_state, custom_attributes) {
    const [chat] = getSelectedChatConversation(_state);
    chat.custom_attributes = custom_attributes;
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const conversation =
      getters.getConversationById(_state)(conversationId) || {};
    conversation.snoozed_until = snoozedUntil;
    conversation.status = status;
  },

  [types.MUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = true;
  },

  [types.UNMUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = false;
  },

  [types.ADD_CONVERSATION_ATTACHMENTS](_state, message) {
    // early return if the message has not been sent, or has no attachments
    if (
      message.status !== MESSAGE_STATUS.SENT ||
      !message.attachments?.length
    ) {
      return;
    }

    const id = message.conversation_id;
    const existingAttachments = _state.attachments[id] || [];

    const attachmentsToAdd = message.attachments.filter(attachment => {
      // if the attachment is not already in the store, add it
      // this is to prevent duplicates
      return !existingAttachments.some(
        existingAttachment => existingAttachment.id === attachment.id
      );
    });

    // replace the attachments in the store
    _state.attachments[id] = [...existingAttachments, ...attachmentsToAdd];
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS](_state, message) {
    if (message.status !== MESSAGE_STATUS.SENT) return;

    const { conversation_id: id } = message;
    const existingAttachments = _state.attachments[id] || [];
    if (!existingAttachments.length) return;

    _state.attachments[id] = existingAttachments.filter(attachment => {
      return attachment.message_id !== message.id;
    });
  },

  [types.ADD_MESSAGE]({ allConversations, selectedChatId }, message) {
    const { conversation_id: conversationId } = message;
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });
    if (!chat) return;

    const pendingMessageIndex = findPendingMessageIndex(chat, message);
    if (pendingMessageIndex !== -1) {
      if (isStaleMessage(chat.messages[pendingMessageIndex], message)) return;
      chat.messages[pendingMessageIndex] = message;
    } else {
      chat.messages.push(message);
      chat.timestamp = message.created_at;
      const { conversation: { unread_count: unreadCount = 0 } = {} } = message;
      chat.unread_count = unreadCount;
      if (selectedChatId === conversationId) {
        emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    }
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    // Teto SÓ do caminho de EVENTO (WebSocket). O fetch paginado
    // (SET_ALL_CONVERSATION) não é afetado: rolar a lista continua trazendo e
    // acumulando 25 por vez, como sempre — quem rola controla o ritmo.
    //
    // Quem não controla é a campanha: 19.785 conversas criadas numa janela da
    // conta 35 (2026-08-07) entraram aqui uma a uma, todas reativas, e
    // ajudaram a travar a aba do atendente. Passado o teto, a inserção
    // automática PARA — não removemos nada do array, porque conversa que some
    // da tela não volta pelo evento seguinte (o `if (!chat) return` do
    // ADD_MESSAGE) e o atendente perderia de vista uma conversa viva.
    //
    // Conversa que não está no array é o caso NORMAL (o array nunca teve todas
    // as da conta): quando o atendente rola, o fetch devolve; quando ele troca
    // de aba ou filtro, a lista é refeita e o crédito volta.
    insertConversationFromEvent(_state, conversation);
  },

  [types.DELETE_CONVERSATION](_state, conversationId) {
    _state.allConversations = _state.allConversations.filter(
      c => c.id !== conversationId
    );
    // Saiu da lista, devolve o crédito — o teto conta o que está EM PÉ.
    _state.realtimeInsertedIds.delete(conversationId);
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    const { allConversations } = _state;
    const index = allConversations.findIndex(c => c.id === conversation.id);

    if (index > -1) {
      const selectedConversation = allConversations[index];

      // ignore out of order events
      if (conversation.updated_at < selectedConversation.updated_at) {
        return;
      }

      const { messages, ...updates } = conversation;
      allConversations[index] = { ...selectedConversation, ...updates };
      // Status/atribuição/etiqueta são atividade: sai da mira do descarte.
      touchRealtimeConversation(_state, conversation.id);
      if (_state.selectedChatId === conversation.id) {
        emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    } else {
      // Inserção EXPLÍCITA, sem passar pelo predicado do render: quem chega
      // aqui é o `getConversation` (atendente abriu uma conversa que não está
      // na lista — uma por vez, ele que pediu, e ele tem que vê-la mesmo fora
      // do filtro atual). A inserção por EVENTO não passa por aqui: a action
      // `updateConversation` desvia pro ADD_CONVERSATION.
      _state.allConversations.push(conversation);
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (chat) {
      chat.agent_last_seen_at = lastSeen;
      chat.unread_count = unreadCount;
    }
  },
  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  // Update assignee on action cable message
  [types.UPDATE_ASSIGNEE](_state, payload) {
    const [chat] = _state.allConversations.filter(c => c.id === payload.id);
    chat.meta.assignee = payload.assignee;
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.meta.sender = payload;
    }
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.can_reply = canReply;
    }
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    _state.allConversations.forEach(c => {
      // Saiu da lista, devolve o crédito — mesma regra do DELETE_CONVERSATION.
      if (c.meta.sender.id === contactId) {
        _state.realtimeInsertedIds.delete(c.id);
      }
    });
    _state.allConversations = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },
  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },
  [types.SET_INBOX_CAPTAIN_ASSISTANT](_state, data) {
    _state.copilotAssistant = data.assistant;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
